export type DivingDad = {
  readonly id: string
  readonly name: string
  readonly role: string
  readonly signatureMove: string
  readonly attemptedMoveIds: readonly string[]
  readonly completedMoveIds: readonly string[]
  readonly aspirationMoveIds: readonly string[]
}

export type PoolMove = {
  readonly id: string
  readonly name: string
  readonly difficulty: 'gentle' | 'ambitious' | 'legendary'
  readonly notes: string
}

export type DivingDadEvent = {
  readonly id: string
  readonly title: string
  readonly when: string
  readonly where: string
  readonly status: 'proposed' | 'confirmed'
  readonly description: string
  readonly details?: readonly string[]
}

export const divingDadsMeta = {
  title: 'Diving Dads',
  tagline: 'Cool crew. Cold pool. Questionable flips.',
  poolName: 'Lake Park',
  intro:
    'A few friends who meet at the pool and cheer each other on. The name says Dads; the guest list does not. Whoever you are — friend, family, neighbor, kid at heart — if you want to splash, you’re already in. Sort-of flips, cannonballs, and whatever counts from the picnic table. Silly on purpose; serious about the stuff that matters.',
  poolCode: [
    'Not a boys’ club. Not exclusive. Whoever wants to be here belongs here — we’ll hold your towel and hype your cannonball.',
    'Cool in the “we will hold your towel and hype your cannonball” sense — not the “we peaked in 1987 and have opinions about your haircut” sense.',
    'The vibe is sunscreen, laughter, and one more try before the lifeguard gives us the look.',
    'Photos and videos land here when we remember to dry our phones.',
    'If it looks like a flip from the picnic table, it counts.'
  ]
} as const

export const upcomingEvents: DivingDadEvent[] = [
  {
    id: 'july4-fundraiser',
    title: 'Lake Park Flip-Off Fundraiser',
    when: 'July 4th weekend, 2026',
    where: 'Lake Park',
    status: 'proposed',
    description:
      'A friendly diving and flipping competition to kick off the season — cannonballs count, form is optional, and every splash helps the cause. Everyone’s invited; bring whoever hypes your jump.',
    details: [
      'Fundraiser format: show up, cheer loud, donate if you can. Open to everyone — no gatekeeping.',
      'Categories TBD (sort-of flip, biggest splash, synchronized splash).',
      'Lifeguard rules apply; we will negotiate with the diving board politely.'
    ]
  }
]

export const divingDadsCrew: DivingDad[] = [
  {
    id: 'dad-tristan',
    name: 'Tristan',
    role: 'A.J.’s younger brother · sort-of flip into the pool',
    signatureMove: 'The sort-of flip (counts in our book)',
    attemptedMoveIds: [
      'cannonball',
      'can-opener',
      'flip-attempt',
      'pencil-dive',
      'jackknife',
      'splash-sync'
    ],
    completedMoveIds: ['cannonball', 'can-opener', 'pencil-dive', 'flip-attempt', 'splash-sync'],
    aspirationMoveIds: ['jackknife', 'twist', 'backward-flip']
  },
  {
    id: 'dad-aj',
    name: 'A.J.',
    role: 'Tristan’s older brother · hype man & sort-of flips',
    signatureMove: 'Running sort-of flip (big splash edition)',
    attemptedMoveIds: [
      'cannonball',
      'can-opener',
      'flip-attempt',
      'belly-flop-classic',
      'splash-sync',
      'jackknife'
    ],
    completedMoveIds: ['cannonball', 'belly-flop-classic', 'flip-attempt', 'splash-sync'],
    aspirationMoveIds: ['can-opener', 'jackknife', 'twist']
  },
  {
    id: 'dad-kiryl',
    name: 'Kiryl',
    role: 'Pool regular · flips, twists & backward flips',
    signatureMove: 'Backward flip',
    attemptedMoveIds: [
      'cannonball',
      'pencil-dive',
      'flip-attempt',
      'jackknife',
      'twist',
      'backward-flip',
      'can-opener',
      'splash-sync'
    ],
    completedMoveIds: [
      'cannonball',
      'pencil-dive',
      'flip-attempt',
      'jackknife',
      'twist',
      'backward-flip',
      'can-opener'
    ],
    aspirationMoveIds: ['splash-sync', 'double-bounce']
  }
]

export const poolMoves: PoolMove[] = [
  {
    id: 'cannonball',
    name: 'Cannonball (classic)',
    difficulty: 'gentle',
    notes: 'Entry-level chaos. Still gets a 10 from the kids.'
  },
  {
    id: 'can-opener',
    name: 'Can opener',
    difficulty: 'ambitious',
    notes: 'Requires confidence and a willingness to apologize to nearby swimmers.'
  },
  {
    id: 'flip-attempt',
    name: 'The flip attempt',
    difficulty: 'legendary',
    notes: 'Sort-of a flip, fully a splash. We count it.'
  },
  {
    id: 'twist',
    name: 'Twist',
    difficulty: 'legendary',
    notes: 'Spin on the way down. Easier to applaud than to explain.'
  },
  {
    id: 'backward-flip',
    name: 'Backward flip',
    difficulty: 'legendary',
    notes: 'Backward is the goal. The diving board is negotiable.'
  },
  {
    id: 'pencil-dive',
    name: 'Pencil dive',
    difficulty: 'gentle',
    notes: 'Straight down, minimal splash, maximum “I meant to do that.”'
  },
  {
    id: 'jackknife',
    name: 'Jackknife',
    difficulty: 'ambitious',
    notes: 'Fold in half, unfold in air, land somewhere in the vicinity of upright.'
  },
  {
    id: 'belly-flop-classic',
    name: 'Belly flop (honest edition)',
    difficulty: 'gentle',
    notes: 'The sound travels faster than the dignity returns.'
  },
  {
    id: 'double-bounce',
    name: 'Double bounce before bail',
    difficulty: 'ambitious',
    notes: 'Two bounces of commitment, then a heroic abort into the shallow end.'
  },
  {
    id: 'underwater-handstand',
    name: 'Underwater handstand',
    difficulty: 'gentle',
    notes: 'Feet in the air, nose in the water, zero flips required.'
  },
  {
    id: 'splash-sync',
    name: 'Synchronized splash (2+ on deck)',
    difficulty: 'legendary',
    notes: 'Timing is optional. Enthusiasm is mandatory. Apologies are shared.'
  }
]

export const getMoveById = (moveId: string): PoolMove | undefined =>
  poolMoves.find((m) => m.id === moveId)

export const getDadsForMove = (
  moveId: string,
  status: 'attempted' | 'completed'
): DivingDad[] =>
  divingDadsCrew.filter((dad) =>
    status === 'completed'
      ? dad.completedMoveIds.includes(moveId)
      : dad.attemptedMoveIds.includes(moveId) &&
        !dad.completedMoveIds.includes(moveId)
  )
